import EditIcon from "@mui/icons-material/Edit";
import { Chip, IconButton } from "@mui/material";
import { MISSING_DECLARATIONS } from "../declarations/declarationConstants";
import {
  EMPLOYEE_TEMP_LABEL,
  FULL_TIME,
  IAM_CONTRACT,
  TWU_CONTRACT,
  EMP_DECLARATION_CREATED_BY_TYPES,
} from "../constants";
import StarsCommentIcon from "../../components/StarsCommentIcon";
import StarsErrorIcon from "../../components/StarsErrorIcon";
import {
  getSortedDeclarationsByEid,
  getSortedDeclarationsByName,
  getSortedDeclarationsByDeclaration,
  getSortedDeclarationsByHireDate,
} from "./sortDeclarations";
import { DateTime } from "luxon";
import { getStationTimeZoneDateTimeStringFromUTCInteger, getTimeZoneFromSwaStation } from "../timeZoneService";
import { getWorkgroupGivenJobCode } from "../workgroupMapper";
import {
  IAM_DECLARATION_BALANCES_IMPORT_ERROR_MESSAGE,
  TWU_DECLARATION_BALANCES_IMPORT_ERROR_MESSAGE,
} from "../bid/messageConstants";
import { parseCSVRow } from "../csvUtils";
import { validateProjectedFTODaysCount, validateProjectedVacationBalanceCount } from "./declarationValidations";
import { getPresignedUrl, uploadCSVFileToS3 } from "../../api/system";
import { sanitizeSpecialCharsIncludingSpaces } from "../commonUtils";

export const getColumns = (props) => {
  let columns = [];

  let baseColumns = [
    {
      field: "actionButton",
      headerName: "",
      renderCell: (params) => {
        return (
          <IconButton color="primary" onClick={() => props.handleEditPencilIconClick(params.row)}>
            <EditIcon />
          </IconButton>
        );
      },
      flex: 0.03,
      sortable: false,
    },
    {
      field: "eid",
      headerName: "EID",
      type: "string",
      flex: 0.11,
      sortComparator: getSortedDeclarationsByEid,
    },
    {
      field: "name",
      headerName: "Name",
      type: "string",
      sortable: true,
      valueGetter: (params) => {
        return {
          lastName: params.row.lastName,
          shortName: params.row.shortName,
          eid: params.row.eid,
        };
      },
      renderCell: (params) => {
        return (
          <div>
            {params.row.lastName}, {params.row.shortName}
            {params.row.isTemp && params.row.homeStation !== props.station && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </div>
        );
      },
      flex: 0.2,
      sortComparator: getSortedDeclarationsByName,
    },
    {
      field: "ftpt",
      headerName: "FT/PT",
      type: "string",
      sortable: false,
      valueGetter: (params) => {
        return {
          ftpt: params.row.ftpt,
        };
      },
      renderCell: (params) => {
        if (!params.row.ftpt) {
          return <StarsErrorIcon />;
        } else {
          return params.row.ftpt;
        }
      },
      flex: 0.1,
    },
    {
      field: "hireDate",
      headerName: "Hire Date",
      type: "string",
      flex: 0.11,
      sortComparator: getSortedDeclarationsByHireDate,
    },
  ];

  let contractBasedColumns = [];
  if (props.contract) {
    if (props.contract === IAM_CONTRACT) {
      contractBasedColumns.push(
        ...[
          {
            field: "projectedVacationBalance",
            headerName: "Projected Vacation Weeks",
            type: "string",
            sortable: false,
            renderCell: (params) => {
              if (!params.row.projectedVacationBalance) {
                return <StarsErrorIcon />;
              } else {
                return (
                  <>
                    {getFormattedProjectedVacationBalance(
                      params.row.unionContract,
                      params.row.ftpt,
                      params.row.projectedVacationBalance
                    )}
                  </>
                );
              }
            },
            flex: 0.25,
          },
          {
            field: "blockWeeksCount",
            headerName: "Block Weeks Declared",
            type: "string",
            sortable: false,
            renderCell: (params) => {
              if (!params.row.blockWeeksCount) {
                return <StarsErrorIcon />;
              } else {
                return params.row.blockWeeksCount;
              }
            },
            flex: 0.25,
          },
          {
            field: "datWeeksCount",
            headerName: "DAT Weeks Declared",
            type: "string",
            sortable: false,
            renderCell: (params) => {
              if (!params.row.datWeeksCount) {
                return <StarsErrorIcon />;
              } else {
                return params.row.datWeeksCount;
              }
            },
            flex: 0.25,
          },
        ]
      );
    } else if (props.contract === TWU_CONTRACT) {
      contractBasedColumns.push(
        ...[
          {
            field: "projectedVacationBalance",
            headerName: "Projected Vacation Weeks",
            type: "string",
            sortable: false,
            renderCell: (params) => {
              if (!params.row.projectedVacationBalance) {
                return <StarsErrorIcon />;
              } else {
                return (
                  <>
                    {getFormattedProjectedVacationBalance(
                      params.row.unionContract,
                      params.row.ftpt,
                      params.row.projectedVacationBalance
                    )}
                  </>
                );
              }
            },
            flex: 0.25,
          },
          {
            field: "projectedFtoBalance",
            headerName: "Projected FTO Days",
            type: "string",
            sortable: false,
            renderCell: (params) => {
              if (!params.row.projectedFtoBalance) {
                return <StarsErrorIcon />;
              } else {
                return <>{getFormattedProjectedFtoBalance(params.row.projectedFtoBalance)}</>;
              }
            },
            flex: 0.25,
          },
          {
            field: "blockWeeksCount",
            headerName: "Block Weeks Declared",
            type: "string",
            sortable: false,
            renderCell: (params) => {
              if (!params.row.blockWeeksCount) {
                return <StarsErrorIcon />;
              } else {
                return params.row.blockWeeksCount;
              }
            },
            flex: 0.25,
          },
          {
            field: "datWeeksCount",
            headerName: "DAT Weeks Declared",
            type: "string",
            sortable: false,
            renderCell: (params) => {
              if (!params.row.datWeeksCount) {
                return <StarsErrorIcon />;
              } else {
                return params.row.datWeeksCount;
              }
            },
            flex: 0.25,
          },
          {
            field: "declaration",
            headerName: "Freeday/FTO",
            type: "string",
            valueGetter: (params) => {
              return {
                declaration: params.row.declaration,
              };
            },
            renderCell: (params) => {
              if (!params.row.declaration) {
                return <StarsErrorIcon />;
              } else {
                return params.row.declaration;
              }
            },
            flex: 0.14,
            sortComparator: getSortedDeclarationsByDeclaration,
          },
        ]
      );
    } else {
      console.log("error unrecognized contract: ", props.contract);
    }
  }
  let extraColumns = [
    {
      headerName: "",
      field: "comments",
      type: "string",
      flex: 0.02,
      sortable: false,
      renderCell: (params) => {
        // show comments icon
        if (
          params?.row?.createdBy === EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_ADMIN ||
          params?.row?.createdBy === EMP_DECLARATION_CREATED_BY_TYPES.CLIENT_EMP
        ) {
          return (
            <StarsCommentIcon
              sx={{ cursor: "pointer" }}
              toolTipMessage={getCommentsTooltipMessage(params.row, props.station)}
              tooltipPlacement="top"
            />
          );
        }
        return <></>;
      },
    },
  ];

  columns.push(...baseColumns);
  columns.push(...contractBasedColumns);
  columns.push(...extraColumns);
  return columns;
};

const getCommentsTooltipMessage = (row, station) => {
  const message = `Updated by ${row?.modifiedByLastName}, ${
    row?.modifiedByFirstName
  } on ${getStationTimeZoneDateTimeStringFromUTCInteger(row?.createdAt, station).toFormat("MM/dd/yyyy T ZZZZ")}`;

  // append comments if available
  if (row?.comments) {
    return `${message} - ${row.comments}`;
  }

  return message;
};

export const filterAllIssuesInDeclarations = (declarationList, unionContract, issueType) => {
  let filteredDeclarations = [];

  if (issueType === MISSING_DECLARATIONS) {
    if (unionContract === IAM_CONTRACT) {
      filteredDeclarations = declarationList.filter(
        (emp) => !emp.projectedVacationBalance || !emp.datWeeksCount || !emp.blockWeeksCount
      );
    } else if (unionContract === TWU_CONTRACT) {
      filteredDeclarations = declarationList.filter(
        (emp) =>
          !emp.declaration ||
          !emp.datWeeksCount ||
          !emp.blockWeeksCount ||
          !emp.projectedVacationBalance ||
          !emp.projectedFtoBalance
      );
    } else {
      return;
    }
  }

  return filteredDeclarations;
};

export const validateDeclaration = (declaration) => {
  if (declaration.trim() === "") {
    return "Declaration is required";
  }
  return "";
};

export const buildDeclarationEvent = (data, userSession = null) => {
  const newBidEvent = {
    createdBy: data.createdBy,
    ...(data.v ? { v: parseInt(data.v) } : {}),
    eid: data.eid,
    declaration: data.declaration,
    station: data.station,
    unionContract: data.unionContract,
    updateType: data.updateType,
    workgroup: data.workgroup,
    year: data.year,
    flags: { isAdminCreated: data.isAdminCreated, isBalanceImport: data.isBalanceImport || false },
    ...(data.comments ? { comments: data.comments } : {}),
    ...(userSession ? { modifiedByEid: userSession.eid } : {}),
    ...(userSession ? { modifiedByFirstName: userSession.firstName } : {}),
    ...(userSession ? { modifiedByLastName: userSession.lastName } : {}),
    ...(userSession ? { modifiedByWorkgroup: getWorkgroupGivenJobCode(userSession.jobCode) } : {}),
    blockWeeksCount: data.blockWeeksCount,
    datWeeksCount: data.datWeeksCount,
    ...(data.projectedVacationBalance ? { projectedVacationBalance: data.projectedVacationBalance } : {}),
    ...(data.projectedFtoBalance ? { projectedFtoBalance: data.projectedFtoBalance } : {}),
  };
  return newBidEvent;
};

export const scrubDeclarationsData = (declarations) => {
  return declarations.map((eachDeclaration) => {
    const {
      homeStation,
      workgroup,
      unionContract,
      eid,
      lastName,
      shortName,
      ftpt,
      hireDate,
      declarationEvent: {
        declaration,
        comments,
        modifiedByFirstName,
        modifiedByLastName,
        createdAt,
        datWeeksCount,
        blockWeeksCount,
        projectedVacationBalance,
        projectedFtoBalance,
        flags,
        createdBy,
      },
    } = eachDeclaration;

    return {
      homeStation,
      workgroup,
      unionContract,
      eid,
      lastName,
      shortName,
      modifiedByFirstName,
      modifiedByLastName,
      createdAt,
      declaration,
      comments,
      datWeeksCount,
      blockWeeksCount,
      projectedVacationBalance,
      projectedFtoBalance,
      flags,
      ftpt,
      createdBy,
      hireDate,
    };
  });
};

export const calculateTimeLeft = (targetDateTime, station) => {
  const currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
  const diff = targetDateTime.diff(currentDateTime);
  if (diff.as("milliseconds") > 0) {
    return {
      days: Math.floor(diff.as("days")),
      hours: Math.floor(diff.as("hours") % 24),
      minutes: Math.floor(diff.as("minutes") % 60),
      seconds: Math.floor(diff.as("seconds") % 60),
    };
  } else {
    return null; // Timer expired
  }
};

export const getFormattedProjectedVacationBalance = (contract, ftpt, projectedVacationBalance) => {
  let weekHours, dayHours;
  const projectedBalance = parseInt(projectedVacationBalance, 10);
  if (contract === IAM_CONTRACT) {
    if (ftpt === FULL_TIME) {
      weekHours = 40;
      dayHours = 8;
    } else {
      weekHours = 30;
      dayHours = 6;
    }
  } else if (contract === TWU_CONTRACT) {
    weekHours = 40;
    dayHours = 8;
  } else {
    return "";
  }

  const weeks = Math.floor(projectedBalance / weekHours);
  const days = Math.floor((projectedBalance % weekHours) / dayHours);

  let formattedText = "";
  if (weeks > 0) {
    formattedText += `${weeks} week${weeks > 1 ? "s" : ""}`;
  }
  if (days > 0) {
    if (formattedText) formattedText += " and ";
    formattedText += `${days} day${days > 1 ? "s" : ""}`;
  }
  if (!formattedText) {
    formattedText = "0 days";
  }
  return formattedText;
};

export const getFormattedProjectedFtoBalance = (projectedFtoBalance) => {
  const projectedBalance = parseInt(projectedFtoBalance);
  // Both FT and PT: 8 hours = 1 day for FTO
  const hoursPerDay = 8;
  const days = Math.floor(projectedBalance / hoursPerDay);
  if (days > 1) {
    return `${days} days`;
  }
  // Ignore partial days as per the requirement
  if (days === 1) {
    return "1 day";
  } else {
    return "0 days";
  }
};

export const getProjectedVacationBalanceWeeks = (contract, ftpt, projectedVacationBalance) => {
  const projectedBalance = parseInt(projectedVacationBalance, 10);
  let weekHours;

  if (contract === IAM_CONTRACT) {
    if (ftpt === FULL_TIME) {
      weekHours = 40;
    } else {
      weekHours = 30;
    }
  } else if (contract === TWU_CONTRACT) {
    weekHours = 40;
  } else {
    return null;
  }

  const weeks = Math.floor(projectedBalance / weekHours);

  if (weeks > 0) {
    return weeks;
  }
  return 0;
};

export const isNextDeclarationYearReadyToDisplay = (station, declarationWindowStartDate) => {
  const currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
  let windowDateTimeBeforeDeclarationOpen = declarationWindowStartDate.minus({
    days: process.env.REACT_APP_ENABLE_DECLARATION_ROUND_YEAR_OPTION_PRIOR_TO_DECLARATION_OPEN,
  });
  return currentDateTime > windowDateTimeBeforeDeclarationOpen;
};

export const isDeclarationWindowClosed = (declarationYear, station, declarationWindowEndDate) => {
  if (declarationYear !== "") {
    let currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
    return currentDateTime.year >= parseInt(declarationYear) ||
      (currentDateTime.year + 1 === parseInt(declarationYear) && currentDateTime > declarationWindowEndDate);
  }
};

export const isBalanceImportButtonDisabled = (
  declarations,
  declarationYear,
  station,
  declarationWindowStartDate,
  declarationWindowEndDate
) => {
  // disable when there are no declarations
  if (!declarations?.length) {
    return true;
  }

  // disable when declaration year is empty
  if (!declarationYear) {
    return true;
  }

  const currentDateTime = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
  // disable for past and present years
  if (currentDateTime.year >= parseInt(declarationYear)) {
    return true;
  }

  // disable for upcoming year when window is not in between 7 days before window start and window end
  if (
    currentDateTime.year + 1 === parseInt(declarationYear) &&
    (currentDateTime <
      declarationWindowStartDate.minus({
        days: process.env.REACT_APP_ENABLE_DECLARATION_ROUND_YEAR_OPTION_PRIOR_TO_DECLARATION_OPEN,
      }) ||
      currentDateTime > declarationWindowEndDate)
  ) {
    return true;
  }

  return false;
};

export const parseTWUDeclarationBalanceImportCSV = (csvText) => {
  const rows = csvText
    .split("\n")
    .map((row) => row.trim())
    .filter((row) => row);

  if (rows.length < 2) {
    return { data: [], error: TWU_DECLARATION_BALANCES_IMPORT_ERROR_MESSAGE };
  }

  const headers = parseCSVRow(rows[0]).map((col) => col.toLowerCase());
  if (
    !headers.includes("eid") ||
    !headers.includes("projected vacation hours") ||
    !headers.includes("projected fto hours")
  ) {
    return { data: [], error: TWU_DECLARATION_BALANCES_IMPORT_ERROR_MESSAGE };
  }

  const data = rows.slice(1).map((row) => {
    const values = parseCSVRow(row);

    return headers.reduce((acc, key, i) => {
      acc[key] = values[i]?.trim() || "";
      return acc;
    }, {});
  });

  return { data, error: null };
};

export const validateTWUDeclarationBalancesCSV = (parsedData, declarations) => {
  const errors = [];
  parsedData.forEach((row, index) => {
    const rowNumber = index + 2;

    if (!/^e\d+$/.test(row.eid)) {
      errors.push(`Row ${rowNumber}: Invalid EID ${row.eid}. Please enter a valid EID including the "e" prefix.`);
      return;
    } else {
      const eidExistsInDeclarations = declarations.find((emp) => emp.eid === row.eid);
      if (!eidExistsInDeclarations) {
        errors.push(`Row ${rowNumber}: Invalid EID ${row.eid}. Please enter an EID from the loaded declaration list.`);
        return;
      }
    }

    const projectedVacationBalance = row["projected vacation hours"];
    const ftpt = declarations.find((emp) => emp.eid === row.eid).ftpt;

    const projectedVacationBalanceErrors = validateProjectedVacationBalanceCount(
      TWU_CONTRACT,
      ftpt,
      projectedVacationBalance
    );
    if (projectedVacationBalanceErrors !== "") {
      errors.push(
        `Row ${rowNumber}: Invalid Projected Vacation Hours entered: ${projectedVacationBalance} for : ${row.eid}. ${projectedVacationBalanceErrors}`
      );
    }

    const projectedFTOBalance = row["projected fto hours"];
    const projectedFTOBalanceErrors = validateProjectedFTODaysCount(projectedFTOBalance);
    if (projectedFTOBalanceErrors !== "") {
      errors.push(
        `Row ${rowNumber}: Invalid Projected FTO Hours entered: ${projectedFTOBalance} for : ${row.eid}. ${projectedFTOBalanceErrors}`
      );
    }
  });

  return errors;
};

export const parseIAMDeclarationBalanceImportCSV = (csvText) => {
  const rows = csvText
    .split("\n")
    .map((row) => row.trim())
    .filter((row) => row);

  if (rows.length < 2) {
    return { data: [], error: IAM_DECLARATION_BALANCES_IMPORT_ERROR_MESSAGE };
  }

  const headers = parseCSVRow(rows[0]).map((col) => col.toLowerCase());

  if (!headers.includes("eid") || !headers.includes("projected vacation hours")) {
    return { data: [], error: IAM_DECLARATION_BALANCES_IMPORT_ERROR_MESSAGE };
  }

  const data = rows.slice(1).map((row) => {
    const values = parseCSVRow(row);

    return headers.reduce((acc, key, i) => {
      acc[key] = values[i]?.trim() || "";
      return acc;
    }, {});
  });

  return { data, error: null };
};

export const validateIAMDeclarationBalancesCSV = (parsedData, declarations) => {
  const errors = [];

  parsedData.forEach((row, index) => {
    const rowNumber = index + 2;
    if (!/^e\d+$/.test(row.eid)) {
      errors.push(`Row ${rowNumber}: Invalid EID ${row.eid}. Please enter a valid EID including the "e" prefix.`);
      return;
    } else {
      const eidExistsInDeclarations = declarations.find((emp) => emp.eid === row.eid);
      if (!eidExistsInDeclarations) {
        errors.push(`Row ${rowNumber}: Invalid EID ${row.eid}. Please enter an EID from the loaded declaration list.`);
        return;
      }
    }

    const projectedVacationBalance = row["projected vacation hours"];
    const ftpt = declarations.find((emp) => emp.eid === row.eid).ftpt;

    const projectedVacationBalanceErrors = validateProjectedVacationBalanceCount(
      IAM_CONTRACT,
      ftpt,
      projectedVacationBalance
    );
    if (projectedVacationBalanceErrors !== "") {
      errors.push(
        `Row ${rowNumber}: Invalid Projected Vacation Hours entered: ${projectedVacationBalance} for : ${row.eid}. ${projectedVacationBalanceErrors}`
      );
    }
  });

  return errors;
};

export const sendDeclarationImportFileToS3 = async (station, workgroup, declarationYear, fileName, fileData) => {
  try {
    const timeZone = await getTimeZoneFromSwaStation(station);
    const s3ObjectName = encodeURIComponent(
      `DECLARATION/${DateTime.now().toFormat("yyyy-MM-dd")}/${station}_${workgroup.replace(
        " ",
        "_"
      )}_${declarationYear}/${DateTime.now().setZone(timeZone).toISO()}_${sanitizeSpecialCharsIncludingSpaces(
        fileName
      )}`
    );

    const res = await getPresignedUrl("put_object", "csv-uploads", s3ObjectName, "text/csv");
    const result = await res.json();
    if (result.presignedUrl) {
      const response = await uploadCSVFileToS3(result.presignedUrl, fileData);
      return response;
    } else {
      return 400;
    }
  } catch (err) {
    console.error("Something went wrong uploading csv file:", err);
  }
};
